import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import TerrainOverlay from './TerrainOverlay'
import { viewshedStore } from '../viewshed/viewshed-store'
import { terrainStore } from './terrain-store'
import { activeFeatureStore } from './active-feature-store'
import { viewportElevExtents, onCacheChange } from './terrain-cache'
import {
  SLOPE_BANDS, ASPECT_DIRS, SOLID_COLORS, DIVERGING, RAMPS,
} from './terrain-utils'
import './terrain-analysis.scss'

// ─── Design tokens ───────────────────────────────────────────────────────────
const T = {
  green:      '#589500',
  greenDim:   '#589500',
  greenGlow:  'rgba(88,149,0,0.4)',
  black:      '#000',
  panelBg:    'rgba(8,9,7,0.92)',
  panelBorder:'rgba(255,255,255,0.08)',
  divider:    'rgba(255,255,255,0.06)',
  textPri:    '#FFFFFF',
  textSec:    'rgba(255,255,255,0.55)',
  textTer:    'rgba(255,255,255,0.4)',
  chip:       'rgba(255,255,255,0.05)',
  chipBorder: 'rgba(255,255,255,0.1)',
}

const gradientFromStops = stops => `linear-gradient(90deg, ${stops.join(', ')})`

// ─── Slope chips ─────────────────────────────────────────────────────────────
function SlopeChips({ selected, onToggle }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
      {SLOPE_BANDS.map((b, i) => {
        const isOn = selected.includes(i)
        const label = b.label || `${b.lo}–${b.hi}°`
        return (
          <button key={i} onClick={() => onToggle(i)} style={{
            padding: '8px 4px', borderRadius: 6,
            border: `1px solid ${isOn ? T.green : T.chipBorder}`,
            background: isOn ? T.green : T.chip,
            color: isOn ? T.black : T.textPri,
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: '0.02em',
            transition: 'all 120ms ease',
          }}>{label}</button>
        )
      })}
    </div>
  )
}

// ─── Aspect compass ──────────────────────────────────────────────────────────
function AspectCompass({ selected, onToggle }) {
  const size = 168, cx = 84, cy = 84, rOuter = 78, rInner = 26

  const wedgePath = (i) => {
    const startDeg = i * 45 - 22.5 - 90
    const endDeg   = startDeg + 45
    const sRad = (startDeg * Math.PI) / 180
    const eRad = (endDeg   * Math.PI) / 180
    const x1o = cx + rOuter * Math.cos(sRad), y1o = cy + rOuter * Math.sin(sRad)
    const x2o = cx + rOuter * Math.cos(eRad), y2o = cy + rOuter * Math.sin(eRad)
    const x1i = cx + rInner * Math.cos(sRad), y1i = cy + rInner * Math.sin(sRad)
    const x2i = cx + rInner * Math.cos(eRad), y2i = cy + rInner * Math.sin(eRad)
    return `M ${x1i} ${y1i} L ${x1o} ${y1o} A ${rOuter} ${rOuter} 0 0 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${rInner} ${rInner} 0 0 0 ${x1i} ${y1i} Z`
  }

  const smallBtn = (active) => ({
    padding: '5px 10px', borderRadius: 4,
    border: `1px solid ${active ? T.green : T.chipBorder}`,
    background: active ? T.green : T.chip,
    color: active ? T.black : T.textPri,
    fontSize: 10, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', letterSpacing: '0.06em', textTransform: 'uppercase',
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width={size} height={size} style={{ flex: '0 0 auto' }}>
        {ASPECT_DIRS.map((dir, i) => {
          const isOn = selected.includes(i)
          return (
            <path key={i} d={wedgePath(i)}
              fill={isOn ? T.green : 'rgba(255,255,255,0.04)'}
              stroke={isOn ? T.greenDim : T.chipBorder} strokeWidth={1}
              style={{ cursor: 'pointer', transition: 'fill 120ms ease' }}
              onClick={() => onToggle(i)} />
          )
        })}
        {ASPECT_DIRS.map((dir, i) => {
          const isOn = selected.includes(i)
          const angle = i * 45 - 90
          const rLabel = (rOuter + rInner) / 2
          const rad = (angle * Math.PI) / 180
          return (
            <text key={`l${i}`}
              x={cx + rLabel * Math.cos(rad)} y={cy + rLabel * Math.sin(rad)}
              textAnchor="middle" dominantBaseline="central"
              fontSize={dir.length === 1 ? 13 : 11} fontWeight="700"
              fill={isOn ? T.black : T.textPri}
              style={{ pointerEvents: 'none', letterSpacing: '0.04em' }}
            >{dir}</text>
          )
        })}
        <circle cx={cx} cy={cy} r={rInner - 4} fill="rgba(255,255,255,0.04)" stroke={T.chipBorder} />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={9} fill={T.textTer} fontWeight="600" letterSpacing="0.1em">FACING</text>
        <text x={cx} y={cy + 8} textAnchor="middle" fontSize={11} fill={T.textPri} fontWeight="700">{selected.length}/8</text>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 8 }}>
          <button onClick={() => onToggle('all')}    style={smallBtn(selected.length === 8)}>All</button>
          <button onClick={() => onToggle('shadedS')} style={smallBtn([3,4,5].every(i => selected.includes(i)) && selected.length === 3)}>S·SE·SW</button>
          <button onClick={() => onToggle('shaded')}  style={{ ...smallBtn([0,1,7].every(i => selected.includes(i)) && selected.length === 3), gridColumn: '1 / -1' }}>Shaded N·NE·NW</button>
        </div>
        <div style={{ fontSize: 11, color: T.textSec, lineHeight: 1.5 }}>
          {selected.length === 0 ? 'No aspect selected'
            : selected.length === 8 ? 'All aspects'
            : selected.map(i => ASPECT_DIRS[i]).join(' · ')}
        </div>
      </div>
    </div>
  )
}

// ─── Elevation slider ────────────────────────────────────────────────────────
function ElevationSlider({ viewMin, viewMax, value, onChange, locked, onToggleLock }) {
  const trackRef = useRef(null)
  const [dragging, setDragging] = useState(null)

  const fmt  = v => `${Math.round(v).toLocaleString()} ft`
  const pct  = v => ((v - viewMin) / (viewMax - viewMin)) * 100

  const onMouseDown = handle => e => {
    if (locked) return
    e.preventDefault()
    setDragging(handle)
  }

  useEffect(() => {
    if (!dragging) return
    const onMove = e => {
      const rect = trackRef.current.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
      const v = Math.round(viewMin + ratio * (viewMax - viewMin))
      if (dragging === 'lo') onChange([Math.min(v, value[1] - 10), value[1]])
      else onChange([value[0], Math.max(v, value[0] + 10)])
    }
    const onUp = () => setDragging(null)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [dragging, value, viewMin, viewMax, onChange])

  const bars = useMemo(() => {
    const arr = [], n = 28
    for (let i = 0; i < n; i++) {
      const x = i / (n - 1)
      arr.push(Math.max(0.05, Math.exp(-Math.pow((x - 0.35) * 2.4, 2)) * 0.85 + Math.random() * 0.15))
    }
    return arr
  }, [viewMin, viewMax]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.textSec }}>
          <span style={{ color: T.textPri, fontWeight: 700, fontSize: 13 }}>{fmt(value[0])}</span>
          <span style={{ margin: '0 6px', color: T.textTer }}>→</span>
          <span style={{ color: T.textPri, fontWeight: 700, fontSize: 13 }}>{fmt(value[1])}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontSize: 9, color: T.textTer, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            View · {fmt(viewMin)}–{fmt(viewMax)}
          </div>
          <button
            onClick={onToggleLock}
            title={locked ? 'Unlock elevation range' : 'Lock to view extremes'}
            style={{
              width: 22, height: 22, borderRadius: 4, padding: 0,
              border: `1px solid ${locked ? T.greenDim : T.chipBorder}`,
              background: locked ? 'rgba(182,255,0,0.15)' : T.chip,
              color: locked ? T.green : T.textTer,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 140ms ease',
            }}
          >
            <svg width={11} height={11} viewBox="0 0 24 24" fill="none">
              <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2.2"/>
              {locked
                ? <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="2.2"/>
                : <path d="M8 11V7a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              }
            </svg>
          </button>
        </div>
      </div>
      <div ref={trackRef} style={{ position: 'relative', height: 44, userSelect: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 1, paddingBottom: 12 }}>
          {bars.map((h, i) => {
            const x = (i / (bars.length - 1)) * 100
            const inRange = x >= pct(value[0]) && x <= pct(value[1])
            return <div key={i} style={{ flex: 1, height: `${h * 100}%`, background: inRange ? T.green : 'rgba(255,255,255,0.12)', borderRadius: 1, transition: 'background 120ms ease' }} />
          })}
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 4, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2 }} />
        <div style={{ position: 'absolute', left: `${pct(value[0])}%`, right: `${100 - pct(value[1])}%`, bottom: 4, height: 4, background: T.green, borderRadius: 2 }} />
        {['lo', 'hi'].map((h, idx) => (
          <div key={h} onMouseDown={onMouseDown(h)} style={{
            position: 'absolute', left: `${pct(value[idx])}%`, bottom: 6,
            transform: 'translate(-50%, 50%)', width: 14, height: 14, borderRadius: 7,
            background: T.green, border: `2px solid ${T.black}`,
            boxShadow: `0 0 0 1px ${T.greenDim}, 0 0 8px ${T.greenGlow}`,
            cursor: locked ? 'default' : 'grab',
            opacity: locked ? 0.45 : 1,
            transition: 'opacity 140ms ease',
          }} />
        ))}
      </div>
    </div>
  )
}

// ─── Opacity slider ──────────────────────────────────────────────────────────
function OpacitySlider({ value, onChange }) {
  const trackRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!dragging) return
    const onMove = e => {
      const rect = trackRef.current.getBoundingClientRect()
      onChange(Math.round(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * 100))
    }
    const onUp = () => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [dragging, onChange])

  return (
    <div ref={trackRef} style={{ position: 'relative', height: 20, cursor: 'pointer', userSelect: 'none' }}
      onMouseDown={e => {
        setDragging(true)
        const rect = trackRef.current.getBoundingClientRect()
        onChange(Math.round(((e.clientX - rect.left) / rect.width) * 100))
      }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 8, height: 4, background: `repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 3px, rgba(255,255,255,0.02) 3px 6px)`, borderRadius: 2 }} />
      <div style={{ position: 'absolute', left: 0, top: 8, height: 4, width: `${value}%`, background: T.green, borderRadius: 2 }} />
      <div style={{ position: 'absolute', left: `${value}%`, top: 10, transform: 'translate(-50%, -50%)', width: 14, height: 14, borderRadius: 7, background: T.green, border: `2px solid ${T.black}`, boxShadow: `0 0 0 1px ${T.greenDim}, 0 0 8px ${T.greenGlow}`, cursor: 'grab' }} />
    </div>
  )
}

// ─── Color mode section ──────────────────────────────────────────────────────
function ColorModeSection({ mode, setMode, palette, setPalette, solid, setSolid, colorBy, setColorBy }) {
  const paletteBtnStyle = active => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 5,
    border: `1px solid ${active ? T.green : T.chipBorder}`,
    background: active ? 'rgba(182,255,0,0.06)' : T.chip, cursor: 'pointer', fontFamily: 'inherit',
  })

  const COLOR_BY_OPTIONS = [
    { value: 'slope',     label: 'SLOPE' },
    { value: 'aspect',    label: 'ASPECT' },
    { value: 'elevation', label: 'ELEV' },
  ]

  return (
    <div>
      {mode !== 'solid' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: T.textTer, textTransform: 'uppercase', marginBottom: 6 }}>Map color to</div>
          <div style={{ display: 'flex', background: T.chip, border: `1px solid ${T.chipBorder}`, borderRadius: 6, padding: 2 }}>
            {COLOR_BY_OPTIONS.map(({ value, label }) => (
              <button key={value} onClick={() => setColorBy(value)} style={{
                flex: 1, padding: '6px 4px', borderRadius: 4, border: 'none',
                background: colorBy === value ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: colorBy === value ? T.textPri : T.textTer,
                fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'all 120ms ease',
              }}>{label}</button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', background: T.chip, border: `1px solid ${T.chipBorder}`, borderRadius: 6, padding: 2, marginBottom: 12 }}>
        {['solid', 'diverging', 'ramp'].map(m => (
          <button key={m} onClick={() => setMode(m)} style={{
            flex: 1, padding: '6px 8px', borderRadius: 4, border: 'none',
            background: mode === m ? T.green : 'transparent',
            color: mode === m ? T.black : T.textSec,
            fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: '0.06em', textTransform: 'uppercase', transition: 'all 120ms ease',
          }}>{m}</button>
        ))}
      </div>
      {mode === 'solid' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SOLID_COLORS.map(c => (
            <button key={c} onClick={() => setSolid(c)} style={{
              width: 32, height: 32, borderRadius: 6,
              border: solid === c ? `2px solid ${T.textPri}` : `1px solid ${T.chipBorder}`,
              background: c, cursor: 'pointer',
              boxShadow: solid === c ? `0 0 0 2px ${T.black}, 0 0 0 3px ${c}` : 'none',
            }} />
          ))}
        </div>
      )}
      {mode === 'diverging' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {DIVERGING.map((p, i) => (
            <button key={p.name} onClick={() => setPalette({ mode: 'diverging', index: i })} style={paletteBtnStyle(palette.mode === 'diverging' && palette.index === i)}>
              <div style={{ flex: 1, height: 16, borderRadius: 3, background: gradientFromStops(p.stops) }} />
              <span style={{ fontSize: 10, color: T.textSec, fontWeight: 600, letterSpacing: '0.04em' }}>{p.name}</span>
            </button>
          ))}
        </div>
      )}
      {mode === 'ramp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {RAMPS.map((p, i) => (
            <button key={p.name} onClick={() => setPalette({ mode: 'ramp', index: i })} style={paletteBtnStyle(palette.mode === 'ramp' && palette.index === i)}>
              <div style={{ flex: 1, height: 16, borderRadius: 3, background: gradientFromStops(p.stops) }} />
              <span style={{ fontSize: 10, color: T.textSec, fontWeight: 600, letterSpacing: '0.04em' }}>{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ label, hint, children }) {
  return (
    <div style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: T.textPri, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 4, height: 4, borderRadius: 2, background: T.green }} />
          {label}
        </div>
        {hint && <div style={{ fontSize: 10, color: T.textTer }}>{hint}</div>}
      </div>
      {children}
    </div>
  )
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const TerrainIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M2 20 L8 10 L12 16 L16 8 L22 20 Z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M8 10 L10 13 M16 8 L18 12" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
  </svg>
)

const ChevronIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M6 9 L12 15 L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// ─── Button styles ────────────────────────────────────────────────────────────
const iconBtnStyle = {
  width: 28, height: 28, borderRadius: 6,
  border: `1px solid ${T.chipBorder}`, background: T.chip,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
}

const FT_PER_METER = 3.28084
const DEFAULT_VIEW = { min: 0, max: 15000 }



// ─── Main component ───────────────────────────────────────────────────────────
export default function TerrainAnalysis({ map }) {
  const [open, setOpen]           = useState(false)
  const [enabled, setEnabled]     = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [applyTrigger, setApplyTrigger] = useState(0)

  // Filters
  const [slopeBands, setSlopeBands] = useState([2, 3, 4])
  const [aspects, setAspects]       = useState([0, 1, 7])
  const [elevation, setElevation]   = useState([DEFAULT_VIEW.min, DEFAULT_VIEW.max])
  const [viewBounds, setViewBounds] = useState(DEFAULT_VIEW)

  // Color
  const [colorMode, setColorMode] = useState('ramp')
  const [palette, setPalette]     = useState({ mode: 'ramp', index: 3 })
  const [solid, setSolid]         = useState('#589500')
  const [colorBy, setColorBy]     = useState('slope')
  const [opacity, setOpacity]     = useState(65)

  // Elevation lock — snaps range to view extremes and follows pan/zoom (on by default)
  const [elevLock, setElevLock] = useState(true)
  const elevLockRef = useRef(true)
  useEffect(() => { elevLockRef.current = elevLock }, [elevLock])

  const handleElevLockToggle = () => {
    const next = !elevLock
    setElevLock(next)
    if (next) setElevation([viewBounds.min, viewBounds.max])
  }

  // Derive view bounds from the decoded terrain tiles covering the viewport.
  // These are the same full-resolution grids the overlay filters against, so the
  // range captures true peaks/valleys instead of undershooting via sparse point
  // sampling (which silently clipped high/low terrain out of the visible tiles).
  const sampleViewBounds = useCallback(() => {
    if (!map) return
    try {
      const b = map.getBounds()
      const extents = viewportElevExtents({
        west:  b.getWest(),
        east:  b.getEast(),
        south: b.getSouth(),
        north: b.getNorth(),
      })
      // No covering tiles cached yet (e.g. just panned to a fresh area). The cache-change
      // subscription will re-invoke this once those tiles finish decoding.
      if (!extents) return
      const vMin = Math.floor(extents.minM * FT_PER_METER)
      const vMax = Math.ceil(extents.maxM * FT_PER_METER)
      if (vMax > vMin) {
        setViewBounds(prev => (prev.min === vMin && prev.max === vMax) ? prev : { min: vMin, max: vMax })
        // Return the SAME array reference when the clamped values are unchanged.
        // The re-apply effect depends on `elevation`; allocating a fresh array on
        // every pan (even with identical values) would re-trigger a full terrain
        // recompute of all visible tiles on each moveend. Bailing out here keeps
        // panning cheap — MapLibre still computes only the newly-revealed tiles.
        if (elevLockRef.current) {
          setElevation(prev => (prev[0] === vMin && prev[1] === vMax) ? prev : [vMin, vMax])
        } else {
          setElevation(prev => {
            const lo = Math.max(vMin, Math.min(prev[0], vMax - 10))
            const hi = Math.min(vMax, Math.max(prev[1], vMin + 10))
            return (lo === prev[0] && hi === prev[1]) ? prev : [lo, hi]
          })
        }
      }
    } catch (_) {}
  }, [map])

  // Debounced moveend listener. Covers panning within already-cached terrain where no
  // new tiles are fetched (so the cache-change listener below wouldn't fire).
  useEffect(() => {
    if (!map) return
    let timer = null
    const handler = () => { clearTimeout(timer); timer = setTimeout(sampleViewBounds, 400) }
    map.on('moveend', handler)
    return () => { map.off('moveend', handler); clearTimeout(timer) }
  }, [map, sampleViewBounds])

  // Re-derive extents as newly-revealed viewport tiles finish decoding. Without this the
  // range stayed frozen when terrain tiles loaded after the moveend debounce had fired.
  useEffect(() => {
    if (!map) return
    let timer = null
    const unsubscribe = onCacheChange(() => {
      clearTimeout(timer)
      timer = setTimeout(sampleViewBounds, 300)
    })
    return () => { unsubscribe(); clearTimeout(timer) }
  }, [map, sampleViewBounds])

  // Sample on panel open; always expand when opening
  useEffect(() => {
    if (open) { sampleViewBounds(); setCollapsed(false) }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSlope  = i => setSlopeBands(s => s.includes(i) ? s.filter(x => x !== i) : [...s, i].sort((a, b) => a - b))
  const toggleAspect = i => {
    if (i === 'all')     setAspects([0, 1, 2, 3, 4, 5, 6, 7])
    else if (i === 'shaded')  setAspects([0, 1, 7])
    else if (i === 'shadedS') setAspects([3, 4, 5])
    else setAspects(a => a.includes(i) ? a.filter(x => x !== i) : [...a, i].sort((a, b) => a - b))
  }

  const filterSummary = useMemo(() => {
    const s = slopeBands.length === 0 ? 'no slopes' : slopeBands.length === 7 ? 'all slopes' : `${slopeBands.length} slope band${slopeBands.length !== 1 ? 's' : ''}`
    const a = aspects.length === 0 ? 'no aspects' : aspects.length === 8 ? 'all aspects' : `${aspects.length} direction${aspects.length !== 1 ? 's' : ''}`
    return `${s} · ${a}`
  }, [slopeBands, aspects])

  const handleFabClick = () => {
    const next = !open
    setOpen(next)
    setEnabled(next)
    if (next && applyTrigger === 0) setApplyTrigger(1)
    activeFeatureStore.set(next ? 'terrain' : null)
  }

  const handleClose = () => {
    setOpen(false)
    setEnabled(false)
    setCollapsed(false)
    activeFeatureStore.set(null)
  }

  // Close when viewshed (or another feature) takes over
  useEffect(() => activeFeatureStore.onChange(() => {
    if (activeFeatureStore.active !== 'terrain') {
      setOpen(false)
      setEnabled(false)
    }
  }), [])

  // Re-render terrain tiles whenever the viewshed changes, but only if terrain
  // is already applied — don't activate the terrain overlay as a side effect.
  useEffect(() => viewshedStore.onChange(() => setApplyTrigger(n => n > 0 ? n + 1 : n)), [])

  // Keep terrainStore in sync so the Viewshed component knows to use combined mode
  useEffect(() => { terrainStore.set(applyTrigger > 0 && enabled) }, [applyTrigger, enabled])

  // After first Apply, re-trigger on any filter change (debounced).
  // Opacity is excluded — TerrainOverlay updates raster-opacity directly without refetching tiles.
  useEffect(() => {
    if (applyTrigger === 0) return
    const timer = setTimeout(() => setApplyTrigger(n => n + 1), 350)
    return () => clearTimeout(timer)
  }, [slopeBands, aspects, elevation, colorMode, palette, solid, colorBy]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filters object forwarded to TerrainOverlay
  const filters = useMemo(() => ({
    slopeBands,
    aspects,
    elevMinM: elevation[0] / FT_PER_METER,
    elevMaxM: elevation[1] / FT_PER_METER,
    colorMode,
    palette,
    solid,
    colorBy,
    opacity,
    enabled,
  }), [slopeBands, aspects, elevation, colorMode, palette, solid, colorBy, opacity, enabled])


  const fabActive  = open
  const fabColor   = fabActive ? '#FFFFFF' : '#6E6E6E'

  return (
    <>
      <TerrainOverlay map={map} filters={filters} applyTrigger={applyTrigger} enabled={enabled} />

      {/* FAB — same row as SAT/HYB/TOPO, 40px to their left */}
      <button
        className={`terrain-fab${fabActive ? ' active' : ''}`}
        onClick={handleFabClick}
        title="Terrain analysis"
      >
        <TerrainIcon size={15} color={fabColor} />
        <span style={{ color: fabColor }}>TERRAIN</span>
      </button>

      {/* Panel */}
      <div
        className="terrain-panel"
        style={{
          transform: open ? 'scale(1) translateY(0)' : 'scale(0.93) translateY(-8px)',
          opacity:   open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: `1px solid ${T.divider}`, background: 'linear-gradient(180deg, rgba(255,255,255,0.02), transparent)' }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: T.green, border: `1px solid ${T.greenDim}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TerrainIcon size={16} color={T.black} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Terrain Analysis</div>
            <div style={{ fontSize: 10, color: T.textSec, marginTop: 2 }}>{filterSummary}</div>
          </div>
          <button onClick={handleClose} style={iconBtnStyle} title="Close">
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
            </svg>
          </button>
          <button onClick={() => setCollapsed(c => !c)} style={iconBtnStyle} title={collapsed ? 'Expand' : 'Collapse'}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {collapsed ? <polyline points="4,9 12,17 20,9" /> : <polyline points="4,15 12,7 20,15" />}
            </svg>
          </button>
        </div>

        {/* Body — flex: 1 + min-height: 0 lets it shrink so footer stays pinned */}
        {!collapsed && <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Section label="Slope angle" hint={`${slopeBands.length} of 7 selected`}>
            <SlopeChips selected={slopeBands} onToggle={toggleSlope} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: T.textTer }}>
              <span>0°</span><span>22°</span><span>45°+</span>
            </div>
          </Section>
          <div style={{ height: 1, background: T.divider }} />
          <Section label="Aspect (slope direction)" hint={`${aspects.length} of 8 directions`}>
            <AspectCompass selected={aspects} onToggle={toggleAspect} />
          </Section>
          <div style={{ height: 1, background: T.divider }} />
          <Section label="Elevation" hint="Updates with map view">
            <ElevationSlider viewMin={viewBounds.min} viewMax={viewBounds.max} value={elevation} onChange={setElevation} locked={elevLock} onToggleLock={handleElevLockToggle} />
          </Section>
          <div style={{ height: 1, background: T.divider }} />
          <Section label="Color" hint={colorMode === 'solid' ? 'Single fill' : colorMode === 'diverging' ? 'Two-ended' : 'Multi-stop'}>
            <ColorModeSection mode={colorMode} setMode={setColorMode} palette={palette} setPalette={setPalette} solid={solid} setSolid={setSolid} colorBy={colorBy} setColorBy={setColorBy} />
          </Section>
          <Section label="Opacity" hint={`${opacity}%`}>
            <OpacitySlider value={opacity} onChange={setOpacity} />
          </Section>
        </div>}

      </div>
    </>
  )
}
